import { getCountryCode } from '@plt/iata-places/country-code'
import type { ConsolidatorRefundRule } from './constants'
import { roundCurrency } from './money'
import type { FareComponentSummary } from './types'

/** GST rate applied to the flat consolidator fee for a wholly-domestic booking in that country. */
const DOMESTIC_GST_RATE: Record<string, number> = { AU: 0.1, NZ: 0.15 }

export interface ConsolidatorFeeContext {
  /** Number of cancelling ADT + CHD passengers. */
  adtChdCount: number
  /** Number of cancelling INF passengers. */
  infCount: number
  /** Ticket issuance commission the consolidator may claw back (booking currency). */
  commission: number
  /**
   * GST rate for the booking's route (0.10 AU-domestic, 0.15 NZ-domestic, 0 otherwise). Resolve
   * with resolveDomesticGSTRate(). Only applied when the rule opts in via gstOnDomestic.
   */
  gstRate: number
  /** Untied PROCFE processing fees included in NettPaid; kept only when the rule opts in. */
  processingFees?: number
}

export interface ConsolidatorFeeBreakdown {
  /** Flat per-pax refund fee. */
  flatFee: number
  /** GST on the flat fee (AU/NZ-domestic bookings, rules that opt in). */
  gst: number
  /** Issuance commission clawed back at the rule's rate. */
  commissionClawback: number
  /** Untied PROCFE processing fees the consolidator keeps (rules with keepsProcessingFees). */
  processingFees: number
  total: number
}

/**
 * The single consolidator-refund-fee calculation. Extensible per rule: flat per-pax fee, optional
 * GST on the flat fee for AU/NZ-domestic bookings, and a configurable commission clawback rate.
 * New per-consolidator behaviours belong on ConsolidatorRefundRule + here, not inlined at call sites.
 */
export function computeConsolidatorRefundFee(
  rule: ConsolidatorRefundRule,
  ctx: ConsolidatorFeeContext,
): ConsolidatorFeeBreakdown {
  const flatFee = roundCurrency(ctx.adtChdCount * rule.ADT + ctx.infCount * rule.INF)
  const gst = roundCurrency(rule.gstOnDomestic ? flatFee * (ctx.gstRate || 0) : 0)
  const commissionClawback = roundCurrency(Math.max(0, ctx.commission) * (rule.commissionClawbackRate ?? 1))
  const processingFees = rule.keepsProcessingFees ? roundCurrency(Math.max(0, ctx.processingFees ?? 0)) : 0
  return {
    flatFee,
    gst,
    commissionClawback,
    processingFees,
    total: roundCurrency(flatFee + gst + commissionClawback + processingFees),
  }
}

/**
 * GST rate for the booking's route derived from the fare-component O&Ds ("SYD-MEL"):
 * 0.10 if every airport is Australian, 0.15 if every airport is New Zealand, 0 otherwise
 * (international, mixed, or any airport we can't resolve to a country).
 */
export function resolveDomesticGSTRate(fareRules: FareComponentSummary[] | undefined): number {
  if (!fareRules?.length) return 0

  const countries = new Set<string>()
  for (const fc of fareRules) {
    for (const iata of (fc.originDestination ?? '').split(/[^A-Za-z]+/).filter(Boolean)) {
      const country = getCountryCode(iata)
      if (!country) return 0 // unknown airport — can't confirm domestic
      countries.add(country)
    }
  }

  if (countries.size !== 1) return 0
  const [only] = [...countries]
  return DOMESTIC_GST_RATE[only] ?? 0
}
